import { create } from "zustand";
import type { Hex } from "@west-marches/shared";
import { hexKey } from "@west-marches/shared";
import type { HexData, MapBounds, MapChangeSnapshot, MapState } from "@west-marches/shared";
import { createInitialMap } from "./createInitialMap";

interface MapStore {
  mapState: MapState | null;
  initMap: (bounds: MapBounds) => void;
  paintHexes: (hexes: HexData[]) => void;
  applyMapChanges: (snapshot: MapChangeSnapshot) => void;
  setParty: (target: Hex) => void;
  applySnapshot: (snapshot: MapChangeSnapshot) => void;
  importMap: (state: MapState) => void;
}

export const useMapStore = create<MapStore>((set, get) => ({
  mapState: null,

  initMap(bounds) {
    set({ mapState: createInitialMap(bounds) });
  },

  paintHexes(hexes) {
    const state = get().mapState;
    if (!state) return;
    const updated = { ...state.hexes };
    for (const h of hexes) {
      const key = hexKey({ q: h.q, r: h.r });
      if (updated[key]) {
        updated[key] = h;
      }
    }
    set({ mapState: { ...state, hexes: updated } });
  },

  applyMapChanges(snapshot) {
    const state = get().mapState;
    if (!state) return;

    const hexes = snapshot.hexes ? { ...state.hexes } : state.hexes;
    if (snapshot.hexes) {
      for (const [key, hex] of Object.entries(snapshot.hexes)) {
        hexes[key] = hex;
      }
    }

    const decorations = snapshot.decorations ? { ...state.decorations } : state.decorations;
    if (snapshot.decorations) {
      for (const [id, decoration] of Object.entries(snapshot.decorations)) {
        if (decoration) {
          decorations[id] = decoration;
        } else {
          delete decorations[id];
        }
      }
    }

    const textLabels = snapshot.textLabels ? { ...state.textLabels } : state.textLabels;
    if (snapshot.textLabels) {
      for (const [id, label] of Object.entries(snapshot.textLabels)) {
        if (label) {
          textLabels[id] = label;
        } else {
          delete textLabels[id];
        }
      }
    }

    set({
      mapState: {
        ...state,
        hexes,
        decorations,
        textLabels,
      },
    });
  },

  setParty(target) {
    const state = get().mapState;
    if (!state) return;
    const key = hexKey(target);
    if (!state.hexes[key]) return;
    set({ mapState: { ...state, partyPosition: target } });
  },

  applySnapshot(snapshot) {
    get().applyMapChanges(snapshot);
  },

  importMap(state) {
    set({ mapState: state });
  },
}));
